/**
 * What every screen needs from the shell: the acting scope, a way to move, and a way to say
 * something happened. One hook so 42 screens do not each reinvent the wiring.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { MODULE_BY_KEY } from './modules';
import { useToast, type ToastRequest } from './toasts';
import { authState, subscribeToAuth } from '../lib/auth/session';
import type { Scope } from '../lib/rbac';

export interface ConsoleApi {
  /**
   * A real scope, never `none`: a screen only renders once the shell has established one, and a
   * scopeless operator is shown the empty console instead of any screen at all.
   */
  scope: Scope;
  /**
   * The signed-in admin's own id, for the screens that act as themselves — taking a ticket,
   * taking a dispute. Read here for the same reason `scope` is: one answer to "who am I",
   * rather than each screen reaching into the session store and forming its own.
   */
  adminId: number;
  /** Move to another module by key, keeping the theme in the URL. */
  go: (key: string, params?: Record<string, string>) => void;
  /**
   * The current route's path parameters.
   *
   * Read here rather than by each detail screen so there is one answer to "which record am I
   * looking at" — a screen that parsed `window.location` itself would be right until the router
   * changed how a path is shaped, and wrong silently after.
   */
  params: Record<string, string>;
  toast: (t: ToastRequest) => void;
}

export function useConsole(): ConsoleApi {
  const navigate = useNavigate();
  /* From the session, not the URL — the same grant the shell shaped the nav with. A screen is
     only rendered once that grant has already opened its door. */
  const auth = useSyncExternalStore(subscribeToAuth, authState);
  const scope = (auth.status === 'signed-in' ? auth.session.scopes[0] : undefined) as Scope;
  const adminId = (auth.status === 'signed-in' ? auth.session.id : undefined) as number;
  const { toast } = useToast();
  /* `strict: false` because this hook is shared by screens with no params at all. */
  const params = useParams({ strict: false }) as Record<string, string>;

  const go = useCallback(
    (key: string, params?: Record<string, string>) => {
      const target = MODULE_BY_KEY[key]!;
      navigate({ to: target.path, params, search: (prev: Record<string, unknown>) => prev });
    },
    [navigate],
  );

  return { scope, adminId, go, toast, params };
}
